import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import type { Policy } from '@/types'

import PolicyListItem from './PolicyListItem'

const PolicyList: FC<{ searchFilter: string }> = ({ searchFilter }) => {
  const translate = useTranslation()
  const { locale } = useAppContext()
  const [queryShowInactive] = useSearchParamsByKey('show_inactive')

  const { data: policyList, isLoading } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )

  const stringMatcher = (item: Policy) => [
    item.name,
    item.description,
    item.vendorName,
    item.customerHeadName
      ? `${translate('Global')}: ${item.customerHeadName}`
      : `${translate('Local')}: ${item.customerName}`,
  ]

  const itemsFiltered =
    policyList?.filter(policy => {
      const querySearchFilter = getQueryFilter(searchFilter, stringMatcher)(policy)
      const activeFilter = queryShowInactive === 'true' ? true : policy.isActive
      return querySearchFilter && activeFilter
    }) ?? []

  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<keyof Policy>()

  const sortedList = getSortedList({
    list: itemsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  if (isLoading) return <LoaderCircle />

  if (visibleList.length === 0) return <NoResults />

  return (
    <>
      <ResultCount count={itemsFiltered?.length || 0} />
      <Table rowHeight="medium">
        <thead>
          <tr>
            <Th id="name" onClick={handleSort}>
              <Translation id="name" />
            </Th>

            <Th id="customerName" onClick={handleSort}>
              <Translation id="customer" />
            </Th>

            <Th id="vendorName" onClick={handleSort}>
              <Translation id="vendor" />
            </Th>

            <Th className="text-right" id="coverAmount" onClick={handleSort}>
              <Translation id="covered amount" />
            </Th>
          </tr>
        </thead>

        <tbody>
          {visibleList.map(item => (
            <PolicyListItem key={item.id} searchFilter={searchFilter} {...item} />
          ))}
        </tbody>
      </Table>
      <ListLoaderComponent />
    </>
  )
}

export default PolicyList
